import { useEffect, useState } from 'react';

export interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** True once the release date has passed. */
  isOver: boolean;
  /** Under a day left: the panel swaps the day figure for seconds. */
  isFinalDay: boolean;
}

function diffToCountdown(target: number, now: number): Countdown {
  const remaining = Math.max(0, target - now);
  const totalSeconds = Math.floor(remaining / 1000);
  const days = Math.floor(totalSeconds / 86400);
  return {
    days,
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    isOver: remaining === 0,
    isFinalDay: days === 0,
  };
}

/**
 * The live clock on the campaign panel, the same one the fan page shows.
 *
 * It ticks every second even while days are still on the board: the panel
 * swaps to hours:minutes:seconds under a day, and a slower timer would leave
 * the seconds frozen for the first tick after the swap.
 */
export function useCountdown(releaseDate: string): Countdown {
  const target = new Date(releaseDate).getTime();
  const [countdown, setCountdown] = useState<Countdown>(() => diffToCountdown(target, Date.now()));

  useEffect(() => {
    setCountdown(diffToCountdown(target, Date.now()));
    if (Number.isNaN(target)) {
      return undefined;
    }
    const interval = setInterval(() => {
      setCountdown(diffToCountdown(target, Date.now()));
    }, 1000);
    return () => clearInterval(interval);
  }, [target]);

  return countdown;
}
